/** Configuration — Form Inventory workflow — validation, save, activate/deactivate */

import { isTeamCoOwnerActor, isTeamOwnerActor } from './teamConfig';
import {
  VALIDATION_MESSAGES,
  conditionRequiresReference,
  conditionRequiresValue,
  createEmptyRule,
  effectRequiresValue,
  enrichRuleFromInventory,
  findDuplicateRuleName,
  findInternalEffectConflicts,
  findOverlappingConflicts,
  getFieldById,
  getFormById,
  getPermittedEffectsForTarget,
  nextConfigurationId,
} from './formInventoryConfigurationConfig';
import { emitRuleSaveHistory } from './changeHistoryEmitters';

export const DEFAULT_ACTOR = 'Platform Owner';

export const buildFormInventoryConfigurationActor = (auth) => {
  if (isTeamOwnerActor(auth)) return 'Platform Owner';
  if (isTeamCoOwnerActor(auth)) return 'Co-owner';
  return auth?.user?.name || auth?.name || DEFAULT_ACTOR;
};

export const canAccessFormInventoryConfiguration = (auth) =>
  isTeamOwnerActor(auth) || isTeamCoOwnerActor(auth);

export const canAddFormInventoryConfiguration = (auth) => canAccessFormInventoryConfiguration(auth);

export const canEditFormInventoryConfiguration = (auth) => canAccessFormInventoryConfiguration(auth);

export const cloneRule = (rule) => JSON.parse(JSON.stringify(rule || createEmptyRule()));

const nowIso = () => new Date().toISOString();

export const collectFieldErrors = (formData, rules = [], forms = [], excludeId) => {
  const errors = {};
  const name = String(formData?.ruleName || '').trim();
  if (!name) errors.ruleName = VALIDATION_MESSAGES.ruleNameMissing;
  else if (findDuplicateRuleName(rules, name, excludeId || formData?.id)) {
    errors.ruleName = VALIDATION_MESSAGES.duplicateRuleName;
  }

  if (!formData?.targetType) errors.targetType = VALIDATION_MESSAGES.targetTypeMissing;
  if (!formData?.targetFormId) errors.targetForm = VALIDATION_MESSAGES.targetFormMissing;

  const form = getFormById(forms, formData?.targetFormId);

  if (formData?.targetType === 'Specific Form Mode' && !formData?.targetMode) {
    errors.targetMode = VALIDATION_MESSAGES.targetModeMissing;
  }

  if (formData?.targetType === 'Specific Form Field') {
    if (!formData?.targetFieldId) {
      errors.targetField = VALIDATION_MESSAGES.targetFieldMissing;
    } else if (form) {
      const field = getFieldById(form, formData.targetFieldId);
      if (!field) errors.targetField = VALIDATION_MESSAGES.fieldBelongsToAnotherForm;
      else if (field.configurationEligibility === 'Not Configurable') {
        errors.targetField = VALIDATION_MESSAGES.fieldNotConfigurable;
      }
    }
  }

  if (!formData?.conditionMatch) errors.conditionMatch = VALIDATION_MESSAGES.conditionMatchMissing;

  const conditions = formData?.conditions || [];
  if (!conditions.length) errors.conditions = VALIDATION_MESSAGES.noConditions;

  conditions.forEach((condition, index) => {
    const prefix = `condition_${index}`;
    if (!condition.source) errors[`${prefix}_source`] = VALIDATION_MESSAGES.conditionSourceMissing;
    if (conditionRequiresReference(condition.source) && !condition.reference) {
      errors[`${prefix}_reference`] = VALIDATION_MESSAGES.conditionReferenceMissing;
    }
    if (!condition.operator) errors[`${prefix}_operator`] = VALIDATION_MESSAGES.conditionOperatorMissing;
    if (conditionRequiresValue(condition.operator)) {
      const multi = Array.isArray(condition.values) && condition.values.length > 0;
      const hasValue = multi || (condition.value != null && String(condition.value).trim() !== '');
      if (!hasValue) errors[`${prefix}_value`] = VALIDATION_MESSAGES.conditionValueMissing;
    }
    if (
      condition.source === 'Form Field Value' &&
      condition.reference &&
      form &&
      !getFieldById(form, condition.reference)
    ) {
      errors[`${prefix}_reference`] = VALIDATION_MESSAGES.unrelatedFormField;
    }
  });

  const effects = formData?.effects || [];
  if (!effects.length) errors.effects = VALIDATION_MESSAGES.noEffects;

  const field =
    formData?.targetType === 'Specific Form Field' && form
      ? getFieldById(form, formData.targetFieldId)
      : null;
  const permitted = getPermittedEffectsForTarget(formData?.targetType, form, field);

  effects.forEach((effect, index) => {
    const prefix = `effect_${index}`;
    if (!effect.effectType) {
      errors[`${prefix}_type`] = VALIDATION_MESSAGES.effectNotPermitted;
      return;
    }
    if (
      formData?.targetType === 'Specific Form Field' &&
      (effect.effectType === 'Allow Submission' || effect.effectType === 'Block Submission')
    ) {
      errors[`${prefix}_type`] = VALIDATION_MESSAGES.submissionEffectOnField;
    } else if (permitted.length && !permitted.includes(effect.effectType)) {
      errors[`${prefix}_type`] = VALIDATION_MESSAGES.effectNotPermitted;
    }
    if (effect.effectType === 'Optional' && field?.baseRequirement === 'Required') {
      errors[`${prefix}_type`] = VALIDATION_MESSAGES.permanentRequirementWeakened;
    }
    if (effectRequiresValue(effect.effectType)) {
      const multi = Array.isArray(effect.effectValues) && effect.effectValues.length > 0;
      const hasValue = multi || (effect.effectValue != null && String(effect.effectValue).trim() !== '');
      if (!hasValue) errors[`${prefix}_value`] = VALIDATION_MESSAGES.effectValueMissing;
    }
  });

  if (findInternalEffectConflicts(effects).length) {
    errors.effects = VALIDATION_MESSAGES.conflictingEffectsInRule;
  }

  const overlaps = findOverlappingConflicts(formData, rules, excludeId || formData?.id);
  if (overlaps.length) {
    errors.overlap = `${VALIDATION_MESSAGES.overlappingRuleConflict} (${overlaps[0].id})`;
  }

  return errors;
};

export const hasValidationErrors = (errors = {}) => Object.keys(errors).length > 0;

export const prepareRuleForSave = (formData, forms = [], actor, { isCreate } = {}) => {
  const form = getFormById(forms, formData.targetFormId);
  const field =
    formData.targetType === 'Specific Form Field' && form
      ? getFieldById(form, formData.targetFieldId)
      : null;
  const stamp = nowIso();
  const base = {
    ...cloneRule(formData),
    ruleName: String(formData.ruleName || '').trim(),
    owningModule: form?.owningModule || formData.owningModule || '',
    targetFormName: form?.formName || formData.targetFormName || '',
    targetMode: formData.targetType === 'Specific Form Mode' ? formData.targetMode : '',
    targetFieldId: formData.targetType === 'Specific Form Field' ? formData.targetFieldId : '',
    targetFieldLabel:
      formData.targetType === 'Specific Form Field'
        ? field?.fieldLabel || formData.targetFieldLabel || ''
        : '',
    targetFieldInternalKey:
      formData.targetType === 'Specific Form Field'
        ? field?.fieldKey || field?.internalFieldKey || formData.targetFieldInternalKey || ''
        : '',
    lastUpdated: stamp,
    lastUpdatedBy: actor || DEFAULT_ACTOR,
  };

  if (isCreate) {
    base.ruleStatus = 'Active';
    base.dateAdded = stamp;
    base.addedBy = actor || DEFAULT_ACTOR;
  }

  return enrichRuleFromInventory(base, forms);
};

export const mergeRulesAfterConfirm = (rules = [], savedRule, { isCreate } = {}) => {
  const previous = isCreate ? null : rules.find((rule) => rule.id === savedRule?.id) || null;
  try {
    emitRuleSaveHistory({
      isNew: Boolean(isCreate),
      previous,
      next: savedRule,
      module: 'Form Inventory',
      sourceModuleRef: 'form_inventory',
      changedItem: savedRule?.ruleName || savedRule?.id,
      itemId: savedRule?.id,
      actor: savedRule?.lastUpdatedBy || DEFAULT_ACTOR,
      stamp: savedRule?.lastUpdated,
      compareKeys: [
        'ruleName',
        'ruleStatus',
        'targetType',
        'targetFormId',
        'targetMode',
        'targetFieldId',
        'conditionMatch',
        'conditions',
        'effects',
      ],
    });
  } catch {
    /* history recording failure must not reverse the source save */
  }
  if (isCreate) {
    return [...rules, savedRule];
  }
  return rules.map((rule) => (rule.id === savedRule.id ? savedRule : rule));
};

export const mergeRulesAfterActivate = (rules = [], ruleId, forms = [], actor) => {
  const stamp = nowIso();
  const previous = rules.find((rule) => rule.id === ruleId);
  const nextRules = rules.map((rule) => {
    if (rule.id !== ruleId) return rule;
    return enrichRuleFromInventory(
      {
        ...rule,
        ruleStatus: 'Active',
        lastUpdated: stamp,
        lastUpdatedBy: actor || DEFAULT_ACTOR,
      },
      forms,
    );
  });
  const next = nextRules.find((rule) => rule.id === ruleId);
  if (previous && next) {
    try {
      emitRuleSaveHistory({
        isNew: false,
        previous,
        next,
        module: 'Form Inventory',
        sourceModuleRef: 'form_inventory',
        changedItem: next.ruleName || ruleId,
        itemId: ruleId,
        actor: actor || DEFAULT_ACTOR,
        stamp,
        compareKeys: ['ruleStatus'],
      });
    } catch {
      /* history recording failure must not reverse the source save */
    }
  }
  return nextRules;
};

export const mergeRulesAfterDeactivate = (rules = [], ruleId, forms = [], actor) => {
  const stamp = nowIso();
  const previous = rules.find((rule) => rule.id === ruleId);
  const nextRules = rules.map((rule) => {
    if (rule.id !== ruleId) return rule;
    return enrichRuleFromInventory(
      {
        ...rule,
        ruleStatus: 'Inactive',
        lastUpdated: stamp,
        lastUpdatedBy: actor || DEFAULT_ACTOR,
      },
      forms,
    );
  });
  const next = nextRules.find((rule) => rule.id === ruleId);
  if (previous && next) {
    try {
      emitRuleSaveHistory({
        isNew: false,
        previous,
        next,
        module: 'Form Inventory',
        sourceModuleRef: 'form_inventory',
        changedItem: next.ruleName || ruleId,
        itemId: ruleId,
        actor: actor || DEFAULT_ACTOR,
        stamp,
        compareKeys: ['ruleStatus'],
      });
    } catch {
      /* history recording failure must not reverse the source save */
    }
  }
  return nextRules;
};

export const assignNewConfigurationId = (rules = [], rule) => ({
  ...rule,
  id: rule.id || nextConfigurationId(rules),
});

export const collectActivationErrors = (rule, rules = [], forms = []) => {
  const errors = collectFieldErrors(rule, rules, forms, rule.id);
  return Object.values(errors);
};
